import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSaasCustomers, type SaasCustomerSummary } from '@/api/backup';
import { useIsAdmin } from '@/api/hooks';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { AppChips, HealthBadge } from '@/components/backup/BackupKit';
import { fmtBytes } from '@/lib/rmm-helpers';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { backupSaasCustomerPath, cippTenantPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { OrbitLink } from '@/components/insights/OrbitLink';

/**
 * Backup → SaaS Protection customers. Every Datto SaaS Protection customer, worst first: per-app
 * health (last 24 h), Datto's backup %, seats by state, archived-but-billed seats, and coverage vs
 * the tenant's licensed M365 users (via CIPP). All from server-side background caches.
 */

type Show = 'all' | 'attention' | 'gaps' | 'waste' | 'unmapped';

const SHOWS: { id: Show; label: string; test: (c: SaasCustomerSummary) => boolean }[] = [
  { id: 'all',       label: 'All customers',        test: () => true },
  { id: 'attention', label: 'Needs attention',      test: (c) => c.health === 'failing' || c.health === 'warning' },
  { id: 'gaps',      label: 'Users not protected',  test: (c) => !!c.coverage?.notProtected },
  { id: 'waste',     label: 'Archived & billed',    test: (c) => !!c.seats?.archivedBillable },
  { id: 'unmapped',  label: 'Not mapped',           test: (c) => c.mapping.companyId == null && c.mapping.source !== 'ignored' },
];

const SHOW_KEY = 'cosmos:backup:saas:show';

export default function BackupSaasCustomersPage() {
  const { data, isLoading, error, isFetching } = useSaasCustomers({ scoped: true });
  const { data: isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [show, setShowState] = useState<Show>(() => {
    try { return (localStorage.getItem(SHOW_KEY) as Show) || 'all'; } catch { return 'all'; }
  });
  const setShow = (s: Show) => { setShowState(s); try { localStorage.setItem(SHOW_KEY, s); } catch { /* storage unavailable */ } };

  // Menu views (?show=…) replace the saved filter, then leave the URL.
  useEffect(() => {
    const s = params.get('show') as Show | null;
    if (!s) return;
    if (SHOWS.some((x) => x.id === s)) setShow(s);
    setParams({}, { replace: true });
  }, [params, setParams]);

  const items = data?.items ?? [];
  const k = useMemo(() => {
    const sum = (f: (c: SaasCustomerSummary) => number) => items.reduce((n, c) => n + f(c), 0);
    const covered = items.filter((c) => c.coverage?.pct != null);
    return {
      failing: items.filter((c) => c.health === 'failing').length,
      warning: items.filter((c) => c.health === 'warning').length,
      seats: sum((c) => c.seats?.total ?? 0),
      billable: sum((c) => c.seats?.billable ?? 0),
      archivedBillable: sum((c) => c.seats?.archivedBillable ?? 0),
      notProtected: sum((c) => c.coverage?.notProtected ?? 0),
      notProtectedCustomers: items.filter((c) => c.coverage?.notProtected).length,
      members: sum((c) => c.coverage?.members ?? 0),
      protectedMembers: sum((c) => c.coverage?.protectedMembers ?? 0),
      coveredCustomers: covered.length,
      storage: sum((c) => c.usedBytes ?? 0),
    };
  }, [items]);

  const counts = useMemo(() => Object.fromEntries(SHOWS.map((s) => [s.id, items.filter(s.test).length])) as Record<Show, number>, [items]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const test = SHOWS.find((x) => x.id === show)!.test;
    return items.filter(test).filter((c) => !s || c.name.toLowerCase().includes(s) || c.domain.toLowerCase().includes(s) || (c.mapping.companyName ?? '').toLowerCase().includes(s));
  }, [items, show, q]);

  const refresh = data?.refresh;
  const coveragePct = k.members ? Math.round((k.protectedMembers / k.members) * 1000) / 10 : null;

  return (
    <DesktopOnly title="Backup is available on desktop" message="Open Cosmos on a larger screen to view backup health.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader
          title="SaaS Protection"
          count={data ? rows.length : null}
          isFetching={isFetching}
          subtitle={data && <>
            Datto SaaS Protection · Microsoft 365
            {refresh?.running
              ? <span className="ml-1.5 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> loading customers {refresh.done}/{refresh.total}</span>
              : refresh?.finishedAt && <> · refreshed {fmtRelative(refresh.finishedAt)} (hourly)</>}
          </>}
          actions={<SearchInput withIcon placeholder="Customer, tenant or company" className="w-64" value={q} onValueChange={setQ} />}
        />

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : error ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Failed to load: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : data ? (
          <>
            {refresh && refresh.loaded < items.length && (
              <div className="mt-3 rounded-md border border-info/30 bg-info-soft px-3 py-2 text-xs text-info">
                Backup detail is still loading for {items.length - refresh.loaded} of {items.length} customers (about 3 minutes after a restart). Rows fill in as it arrives.
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 min-[1400px]:grid-cols-6">
              <StatCard label="Customers" value={items.length} sub={`${fmtBytes(k.storage)} backed up`} onClick={() => setShow('all')} />
              <StatCard label="Failing" value={k.failing} sub={`${k.warning} with issues`} tone={k.failing ? 'destructive' : k.warning ? 'warning' : 'success'} onClick={() => setShow('attention')} />
              <StatCard label="M365 coverage" value={coveragePct != null ? `${coveragePct}%` : '—'} sub={`across ${k.coveredCustomers} tenants`}
                tone={coveragePct == null ? 'default' : coveragePct >= 99 ? 'success' : 'warning'} onClick={() => setShow('gaps')} />
              <StatCard label="Users not protected" value={k.notProtected} sub={`in ${k.notProtectedCustomers} tenants`} tone={k.notProtected ? 'warning' : 'success'} onClick={() => setShow('gaps')} />
              <StatCard label="Archived & billed" value={k.archivedBillable} sub="deleted users still billed" tone={k.archivedBillable ? 'warning' : 'success'} onClick={() => setShow('waste')} />
              <StatCard label="Seats" value={k.seats} sub={`${k.billable} billable`} />
            </div>

            <div className="mt-4 flex flex-wrap gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
              {SHOWS.map((s) => (
                <button key={s.id} type="button" onClick={() => setShow(s.id)}
                  className={cn('rounded-md px-3 py-1 text-xs font-medium transition-colors',
                    show === s.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  {s.label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[s.id]}</span>
                </button>
              ))}
            </div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
              <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 1100 }}>
                {/* Fixed widths that fit 1440px: app chips sit 2 × 2, seats/coverage take two short lines, the company keeps ~180px. */}
                <colgroup>
                  <col style={{ width: 220 }} /><col style={{ width: 90 }} /><col style={{ width: 172 }} /><col style={{ width: 80 }} />
                  <col style={{ width: 190 }} /><col style={{ width: 130 }} /><col style={{ width: 84 }} /><col />
                </colgroup>
                <thead className="whitespace-nowrap bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Health</th>
                    <th className="px-3 py-2 font-medium">Apps · last 24 h</th>
                    <th className="px-3 py-2 text-right font-medium" title="Datto: active services with a recent backup">Backup %</th>
                    <th className="px-3 py-2 font-medium">Seats</th>
                    <th className="px-3 py-2 font-medium" title="Licensed, enabled M365 members with a protected seat (via CIPP)">M365 coverage</th>
                    <th className="px-3 py-2 text-right font-medium">Storage</th>
                    <th className="px-3 py-2 font-medium">Autotask company</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">Nothing matches.</td></tr>}
                  {rows.map((c) => (
                    <tr key={c.saasCustomerId} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(backupSaasCustomerPath(c.saasCustomerId))}>
                      <td className="overflow-hidden px-3 py-2">
                        <div className="truncate font-medium text-foreground" title={c.name}>{c.name}</div>
                        <div className="truncate text-[11px] text-muted-foreground">{c.domain}.onmicrosoft.com{c.retentionType === 'TBR' && ' · time-based retention'}</div>
                      </td>
                      <td className="px-3 py-2">{c.seats
                        ? <HealthBadge health={c.health} title={c.error ? `Datto error: ${c.error}` : undefined} />
                        : <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}</td>
                      <td className="px-3 py-2"><AppChips apps={c.apps} /></td>
                      <td className={cn('px-3 py-2 text-right tabular-nums', c.backupPct != null && c.backupPct < 95 && 'font-medium text-destructive')}>
                        {c.backupPct != null ? `${c.backupPct}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums">
                        {c.seats ? (
                          <>
                            <div><span className="whitespace-nowrap"><span className="text-foreground">{c.seats.byState.Active ?? 0}</span> active</span>
                            {c.seats.byState.Archived ? <span className="whitespace-nowrap text-muted-foreground"> · {c.seats.byState.Archived} archived</span> : null}</div>
                            {c.seats.archivedBillable ? <div className="whitespace-nowrap text-warning" title={`${c.seats.archivedBillable} archived seats still billed`}>{c.seats.archivedBillable} still billed</div> : null}
                          </>
                        ) : <span className="text-muted-foreground">{c.seatsUsed} seats</span>}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums">
                        {c.coverage ? (
                          <span title={`${c.coverage.protectedMembers} of ${c.coverage.members} licensed users protected`}>
                            <span className={cn('font-medium', c.coverage.notProtected ? 'text-warning' : 'text-foreground')}>{c.coverage.pct ?? '—'}%</span>
                            {c.coverage.notProtected > 0 && <div className="whitespace-nowrap text-muted-foreground">{c.coverage.notProtected} not protected</div>}
                          </span>
                        ) : <span className="text-muted-foreground">{c.mapping.tenant ? '—' : 'not in CIPP'}</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtBytes(c.usedBytes)}</td>
                      <td className="overflow-hidden px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                        {c.mapping.companyName ? (
                          <div className="truncate" title={c.mapping.companyName}>
                            <OrbitLink companyId={c.mapping.companyId} className="text-foreground">{c.mapping.companyName}</OrbitLink>
                            <span className="ml-1 text-muted-foreground">
                              {c.mapping.source === 'cipp' && c.mapping.tenant
                                ? <>· <Link to={cippTenantPath(c.mapping.tenant.customerId)} className="hover:underline">via CIPP</Link></>
                                : c.mapping.source === 'cipp' ? '· via CIPP' : '· manual'}
                            </span>
                          </div>
                        ) : c.mapping.source === 'ignored' ? <span className="italic text-muted-foreground">ignored</span>
                          : <Link to={backupSaasCustomerPath(c.saasCustomerId)} className="text-warning hover:underline">{isAdmin ? 'Not mapped — map it' : 'Not mapped'}</Link>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Health: <strong>Failing</strong> = under 80% of an app's services backed up in the last 24 h; <strong>Issues</strong> = some services
              missed or had item-level problems, or the app hasn't been fully protected for 24 h+. Customers map to Autotask through their
              CIPP tenant (matched on the onmicrosoft domain).
            </p>
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}
